import { BaseViewStore } from 'core/stores';
import { OnHoldForm } from 'modules/order/forms';
import { observable, action, makeObservable } from 'mobx';
import { FulfillmentOrderStore } from 'modules/order/stores';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, sourceOfChange } from 'common/constants';
import { isSome, notificationError, translate } from 'core/utils';
import { fulfillmentOrderStatus, onHoldReason, onHoldStatuses } from 'common/constants';
import _ from 'lodash';
import { BaasicDropdownViewStore } from 'common/stores';

class OnHoldViewStore extends BaseViewStore {
	@observable selectedOnHoldReason = null;
	@observable onHoldOptions = [];
	@observable isOnHoldReasonLoading = false;
	selectedItem = null;
	selectedItems = null;
	isEnteredStatus = false;
	debouncedOnHoldSearch = null;

	form = new OnHoldForm({
		onSuccess: async () => {
			if (this.selectedItems == null) {
				await this.onHold();
			} else {
				await this.onHoldBulk();
			}
		},
	});

	constructor(rootStore, { parentViewStore, selectedItemId, selectedItemIds }) {
		super(rootStore);
		makeObservable(this);
		this.parentView = parentViewStore;
		this.lookupStore = new LookupStore(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);

		this.initialize(selectedItemId, selectedItemIds);
	}

	@action.bound
	initialize = async (selectedItemId, selectedItemIds) => {
		this.suspend();
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		if (selectedItemId != null) {
			await this.getFulfillmentOrder(selectedItemId);
		} else {
			await this.findFulfillmentOrders(selectedItemIds);
		}
		this.onHoldReasonDropdownStore = new BaasicDropdownViewStore({
			store: new LookupStore(this.rootStore),
			value: this.selectedOnHoldReason,
			lookupRoute: lookupRouteName.onHoldReason,
			filterCallback: this.filterOnHoldReason,
			onChangeCallback: this.onChange,
		});
		this.resume();
	};

	@action.bound
	onChange = item => {
		this.selectedOnHoldReason = item;
		if (item != null) {
			this.form.$('onHoldReasonId').value = item.value;
		} else {
			this.form.$('onHoldReasonId').clear();
		}
	};

	@action.bound
	findFulfillmentOrders = async selectedItemIds => {
		this.selectedItems = (await this.fulfillmentOrderStore.findFulfillmentOrder({
			page: 1,
			rpp: 100,
			ids: selectedItemIds,
			embed: ['fulfillmentOrderStatus', 'onHoldReason'],
		})).item;

		let selectedItemsWithOnHoldReason = this.selectedItems.filter(item => isSome(item.onHoldReason));

		if (selectedItemsWithOnHoldReason != null && selectedItemsWithOnHoldReason.length > 0) {
			let onHoldReason = selectedItemsWithOnHoldReason[0].onHoldReason;
			let selectedItemsWithSameOnHoldReason = selectedItemsWithOnHoldReason.filter(
				item => item.onHoldReasonId == onHoldReason.id
			);

			if (selectedItemsWithSameOnHoldReason.length == selectedItemsWithOnHoldReason.length) {
				this.selectedOnHoldReason = {
					value: onHoldReason.id,
					label: onHoldReason.name,
				};
			} else {
				this.selectedOnHoldReason = {
					value: '',
					label: '',
				};
			}
		}
	};

	@action.bound
	getFulfillmentOrder = async selectedItemId => {
		this.selectedItem = await this.fulfillmentOrderStore.getFulfillmentOrder(selectedItemId, {
			embed: ['fulfillmentOrderStatus', 'onHoldReason'],
		});
		if (this.selectedItem.onHoldReason != null) {
			this.selectedOnHoldReason = {
				value: this.selectedItem.onHoldReasonId,
				label: this.selectedItem.onHoldReason.name,
			};
			this.form.$('onHoldReasonId').value = this.selectedItem.onHoldReasonId;
		}
		this.isEnteredStatus = this.selectedItem.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.entered;
	};

	@action.bound
	filterOnHoldReason(options = []) {
		if (this.isEnteredStatus) {
			const optionIdx = _.findIndex(options, item => {
				return item.abrv == onHoldReason.expensiveService;
			});
			if (optionIdx >= 0) {
				options.splice(optionIdx, 1);
			}
		}

		return options.map(item => {
			return {
				value: item.id,
				label: item.name,
				extra: item,
			};
		});
	}

	@action.bound async onHold() {
		try {
			const { onHoldReasonId } = this.form.values();
			const onHoldParams = { fulfillmentOrderId: this.selectedItem.id, onHoldReasonId: onHoldReasonId };
			await this.fulfillmentOrderStore.onHold(onHoldParams, sourceOfChange.onHoldAction);
			if (this.selectedOnHoldReason != null) {
				this.rootStore.notificationStore.success(['ONHOLDVIEW.SUCCESS', { id: this.selectedItem.fulfillmentOrderId }]);
			} else {
				this.rootStore.notificationStore.success([
					'ONHOLDVIEW.CLEAREDONHOLD',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			}
			this.parentView.reload = true;
			this.parentView.onHoldModal.close();
		} catch (err) {
			if (err.statusCode == 400 && err.data == 'FULFILLMENTORDER.ALREADYPROCESSING') {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.ALREADYPROCESSING',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
	}

	@action.bound async onHoldBulk() {
		try {
			const { onHoldReasonId } = this.form.values();
			const selectedOrderIds = [];
			const notProcessableOrders = [];
			this.selectedItems.map(selectedItem => {
				let index = _.findIndex(onHoldStatuses, item => item == selectedItem.fulfillmentOrderStatus.abrv);
				if (index != -1) {
					selectedOrderIds.push(selectedItem.id);
				} else {
					notProcessableOrders.push(selectedItem);
				}
			});
			this.parentView.selectedItems.forEach(o => delete o.isChecked);
			this.parentView.selectedItems = [];
			this.parentView.isSelectAllCheckboxSelected = false;
			if (this.parentView.selectedGroupByOption) {
				this.parentView.selectedGroups.forEach(g => delete g.isChecked);
				this.parentView.selectedGroups = [];
			}
			const onHoldBulkParams = { fulfillmentOrderIds: selectedOrderIds, onHoldReasonId: onHoldReasonId };
			this.parentView.onHoldBulkModal.close();
			const notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.ONHOLD'));
			const count = await this.fulfillmentOrderStore.onHoldBulk(onHoldBulkParams, sourceOfChange.onHoldBulkAction);
			this.rootStore.notificationStore.dismiss(notificationId);
			if (count > 0) {
				if (count == selectedOrderIds.length) {
					if (this.selectedOnHoldReason != null) {
						this.rootStore.notificationStore.success(['ONHOLDVIEW.SUCCESSBULK', { count: count }]);
					} else {
						this.rootStore.notificationStore.success(['ONHOLDVIEW.CLEAREDONHOLDBULK', { count: count }]);
					}
				} else {
					const totalOrdersNotUpdated = selectedOrderIds.length - count + notProcessableOrders.length;
					this.rootStore.notificationStore.success(['ONHOLDVIEW.PARTIALSUCCESSBULK', { count: totalOrdersNotUpdated }]);
				}
			} else {
				this.rootStore.notificationStore.error(['ONHOLDVIEW.NOVALIDORDERSELECTED', { count: count }]);
			}
			this.parentView.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	}
}

export default OnHoldViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/OnHoldViewStore.js